import axios from 'axios';

// Always use current origin to ensure API calls work on any domain
const API_URL = window.location.origin;

const api = axios.create({
  baseURL: `${API_URL}/api`,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors — never force-logout via hard redirect on individual requests.
// Transient 401s (MongoDB stall, ingress glitch, CORS preflight race, missing
// Authorization header) used to call window.location.href = '/login' which ejected
// users mid-session with a confusing "Errore di autenticazione" toast. Instead we
// delegate all logout decisions to AuthContext.checkAuth(), which validates the
// JWT locally (no API call required) and is the single source of truth.
let lastAuthErrorToast = 0;
const BACKGROUND_POLL_PATHS = [
  '/chat/online-users',
  '/chat/heartbeat',
  '/notifications',
  '/push/',
];

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    const reqUrl = error.config?.url || '';
    const isBackgroundPoll = BACKGROUND_POLL_PATHS.some(p => reqUrl.includes(p));
    if (isBackgroundPoll) {
      // Never propagate background poll 401s
      return Promise.reject(error);
    }

    // Throttled soft diagnostic — NO hard redirect. If the JWT truly expired,
    // AuthContext.checkAuth() will detect it locally and log the user out
    // cleanly. If it's a transient glitch, the next request will succeed and
    // the user never notices.
    const now = Date.now();
    if (now - lastAuthErrorToast > 10000) {
      lastAuthErrorToast = now;
      try {
        console.warn('401 on', reqUrl, '- delegating to AuthContext');
      } catch (_) {}
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  discordLinkUrl: () => api.get('/auth/discord/link'),
  discordUnlink: () => api.delete('/auth/discord/link'),
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  session: (data) => api.post('/auth/session', data),
  completeProfile: (data) => api.post('/auth/complete-profile', data),
  uploadProfilePicture: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/auth/profile-picture', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteProfilePicture: () => api.delete('/auth/profile-picture'),
  getProfilePictureCost: () => api.get('/auth/profile-picture/cost'),
  changeGamertag: (newGamertag) => api.post('/auth/change-gamertag', { new_gamertag: newGamertag }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
};

// Clubs
export const eaApi = {
  getAutoReport: () => api.get('/ea/auto-report'),
  toggleAutoReport: (enabled) => api.put('/ea/auto-report', { enabled }),
  runAutoReport: () => api.post('/ea/auto-report/run'),
  search: (name, platform = 'common-gen5') => api.get('/ea/search', { params: { name, platform } }),
  linkClub: (clubId, body) => api.post(`/clubs/${clubId}/ea-link`, body),
  unlinkClub: (clubId) => api.delete(`/clubs/${clubId}/ea-link`),
  clubStats: (clubId) => api.get(`/clubs/${clubId}/ea-stats`),
  setGamertag: (eaGamertag) => api.put('/ea/my-gamertag', { ea_gamertag: eaGamertag }),
  playerStats: (userId) => api.get(`/users/${userId}/ea-stats`),
  verifyMatch: (matchId) => api.get(`/matches/${matchId}/ea-verify`),
};

export const clubsApi = {
  list: (params) => api.get('/clubs', { params }),
  listLight: (params) => api.get('/clubs', { params: { ...params, include_logo: false } }),
  get: (id) => api.get(`/clubs/${id}`),
  create: (data) => api.post('/clubs', data),
  update: (id, data) => api.put(`/clubs/${id}`, data),
  delete: (id) => api.delete(`/clubs/${id}`),
  uploadLogo: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/clubs/${id}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getLogoCost: (id) => api.get(`/clubs/${id}/logo-cost`),
  invite: (clubId, userId) => api.post(`/clubs/${clubId}/invite/${userId}`),
  join: (id) => api.post(`/clubs/${id}/join`),
  release: () => api.post('/clubs/release'),
  getReleaseCost: () => api.get('/clubs/release-cost'),
  getMembers: (id) => api.get(`/clubs/${id}/members`),
  assignRole: (clubId, userId, roleType) => api.put(`/clubs/${clubId}/assign-role`, { user_id: userId, role_type: roleType }),
  releasePlayer: (clubId, playerId) => api.post(`/clubs/${clubId}/release-player/${playerId}`),
  setViceCaptain: (clubId, playerId) => api.post(`/clubs/${clubId}/vice-captain/${playerId}`),
  removeViceCaptain: (clubId, playerId) => api.delete(`/clubs/${clubId}/vice-captain`, { params: playerId ? { player_user_id: playerId } : {} }),
};

// Transfer Requests (Player to Club)
export const transferRequestsApi = {
  create: (data) => api.post('/transfer-requests', data),
  getMyRequests: () => api.get('/transfer-requests/my-requests'),
  getClubRequests: (clubId) => api.get(`/transfer-requests/club/${clubId}`),
  respond: (id, accept) => api.put(`/transfer-requests/${id}/respond?accept=${accept}`),
  cancel: (id) => api.delete(`/transfer-requests/${id}`),
  getCost: () => api.get('/transfer-requests/cost'),
};

// Players
export const playersApi = {
  list: (params) => api.get('/players', { params }),
  get: (id) => api.get(`/players/${id}`),
  create: (data) => api.post('/players', data),
  delete: (id) => api.delete(`/players/${id}`),
  toggleForSale: (id, price) => api.put(`/players/${id}/for-sale`, null, { params: { price } }),
};

// Transfers
export const transfersApi = {
  list: (params) => api.get('/transfers', { params }),
  create: (data) => api.post('/transfers', data),
  respond: (id, accept) => api.put(`/transfers/${id}/respond`, null, { params: { accept } }),
};

// Market
export const marketApi = {
  getStatus: () => api.get('/market/status'),
  open: () => api.post('/market/open'),
  close: (message) => api.post('/market/close', null, { params: { message } }),
  schedule: (data) => api.post('/market/schedule', data),
};

// Matches
export const matchesApi = {
  list: (params) => api.get('/matches', { params }),
  get: (id) => api.get(`/matches/${id}`),
  getByChampionship: (championshipId) => api.get('/matches', { params: { championship_id: championshipId } }),
  create: (data) => api.post('/matches', data),
  submitReport: (id, data) => api.post(`/matches/${id}/report`, data),
  resync: (id) => api.post(`/matches/${id}/resync`),
  resyncAllPending: () => api.post('/matches/resync-all-pending'),
  cancelReport: (id) => api.post(`/matches/${id}/cancel-report`),
  updateDate: (id, matchDate) => api.put(`/matches/${id}/date`, { match_date: matchDate }),
  adminSetResult: (id, homeScore, awayScore) => api.post(`/matches/${id}/admin-set-result`, { home_score: homeScore, away_score: awayScore }),
  getReportsLock: () => api.get('/matches/reports-lock'),
  toggleReportsLock: () => api.put('/matches/reports-lock'),
  requestJolly: (matchId, proposedDate, clubId) => api.post(`/matches/${matchId}/request-jolly`, { proposed_date: proposedDate, ...(clubId && { club_id: clubId }) }),
  getJollyInfo: (matchId) => api.get(`/matches/${matchId}/jolly-info`),
  getPhotos: (matchId) => api.get(`/matches/${matchId}/photos`),
  bulkUpdateDate: (data) => api.post('/matches/bulk-update-date', data),
  bulkUpdateDays: (updates) => api.post('/matches/bulk-update-days', { updates }),
  swapMatchDays: (championshipId, dayA, dayB) => api.post('/matches/swap-match-days', { championship_id: championshipId, day_a: dayA, day_b: dayB }),
  swapMatches: (matchA, matchB) => api.post('/matches/swap-matches', { match_a: matchA, match_b: matchB }),
  updateMatchDay: (matchId, matchDay) => api.put(`/matches/${matchId}/match-day`, { match_day: matchDay }),
  // Match Chat (capitani + vice)
  getMatchChat: (matchId) => api.get(`/matches/${matchId}/chat`),
  postMatchChat: (matchId, content) => api.post(`/matches/${matchId}/chat`, { content }),
  markMatchChatRead: (matchId) => api.post(`/matches/${matchId}/chat/read`),
  markAllMatchChatsRead: () => api.post('/match-chats/read-all'),
  listMyMatchChats: () => api.get('/match-chats/my'),
  getMatchChatsUnread: () => api.get('/match-chats/unread-count'),
  getPendingJolly: () => api.get('/jolly/pending'),
  getApprovedJolly: () => api.get('/jolly/approved'),
  approveJolly: (requestId) => api.put(`/jolly/${requestId}/approve`),
  rejectJolly: (requestId) => api.put(`/jolly/${requestId}/reject`),
  cancelJolly: (requestId) => api.put(`/jolly/${requestId}/cancel`),
  resolveDispute: (matchId, data) => api.post(`/matches/${matchId}/resolve-dispute`, data),
  editReport: (matchId, data) => api.put(`/matches/${matchId}/edit-report`, data),
};

export const friendsApi = {
  // Friendship
  request: (recipientId) => api.post('/friends/request', { recipient_id: recipientId }),
  accept: (friendshipId) => api.post(`/friends/${friendshipId}/accept`),
  reject: (friendshipId) => api.post(`/friends/${friendshipId}/reject`),
  remove: (friendshipId) => api.delete(`/friends/${friendshipId}`),
  list: () => api.get('/friends'),
  requests: () => api.get('/friends/requests'),
  search: (q) => api.get('/friends/search', { params: { q } }),
  unreadCount: () => api.get('/friends/unread-count'),
  // Private chat
  getChat: (friendshipId) => api.get(`/friends/${friendshipId}/chat`),
  sendMessage: (friendshipId, content) => api.post(`/friends/${friendshipId}/chat`, { content }),
  markRead: (friendshipId) => api.post(`/friends/${friendshipId}/chat/read`),
};

// Championships
export const championshipsApi = {
  list: (params) => api.get('/championships', { params }),
  listAdmin: () => api.get('/admin/championships'),
  get: (id) => api.get(`/championships/${id}`),
  create: (data) => api.post('/championships', data),
  delete: (id) => api.delete(`/championships/${id}`),
  join: (id, accessKey) => api.post(`/championships/${id}/join`, { access_key: accessKey }),
  start: (id) => api.post(`/championships/${id}/start`),
  standings: (id, group) => api.get(`/championships/${id}/standings`, { params: group ? { group } : {} }),
  topScorers: (id, limit) => api.get(`/championships/${id}/top-scorers`, { params: { limit } }),
  topAssists: (id, limit) => api.get(`/championships/${id}/top-assists`, { params: { limit } }),
  clubGoals: (id) => api.get(`/championships/${id}/club-goals`),
  updateStatus: (id, status) => api.put(`/championships/${id}/status`, { status }),
  getClubs: (id) => api.get(`/championships/${id}/clubs`),
  addClub: (championshipId, clubId) => api.post(`/championships/${championshipId}/add-club/${clubId}`),
  removeClub: (championshipId, clubId) => api.delete(`/championships/${championshipId}/clubs/${clubId}`),
  generateSchedule: (championshipId, legs = null) => api.post(`/championships/${championshipId}/generate-schedule`, legs ? { legs } : {}),
  regenerateKey: (championshipId) => api.post(`/championships/${championshipId}/regenerate-key`),
  updateMaxTeams: (championshipId, maxTeams) => api.put(`/championships/${championshipId}/max-teams`, { max_teams: maxTeams }),
  getFixtures: (id, matchDay, { skip, limit } = {}) => api.get(`/championships/${id}/fixtures`, { params: { ...(matchDay ? { match_day: matchDay } : {}), ...(skip != null ? { skip } : {}), ...(limit ? { limit } : {}) } }),
  getJollyStatus: (id) => api.get(`/championships/${id}/jolly-status`),
  withdrawClub: (championshipId, clubId) => api.post(`/championships/${championshipId}/withdraw-club/${clubId}`),
  reinstateClub: (championshipId, clubId) => api.post(`/championships/${championshipId}/reinstate-club/${clubId}`),
  replaceClub: (championshipId, oldClubId, newClubId) => api.post(`/championships/${championshipId}/replace-club/${oldClubId}/${newClubId}`),
  reorderMatchdays: (championshipId) => api.post(`/championships/${championshipId}/reorder-matchdays`),
  reorderMatchdaysByDate: (championshipId) => api.post(`/championships/${championshipId}/reorder-matchdays-by-date`),
  realignMatchdaysByRound: (championshipId) => api.post(`/championships/${championshipId}/realign-matchdays-by-round`),
  groupMatchdaysByDate: (championshipId) => api.post(`/championships/${championshipId}/group-matchdays-by-date`),
  swapClubInMatches: (championshipId, oldClubId, newClubId) => api.post(`/championships/${championshipId}/swap-club-in-matches/${oldClubId}/${newClubId}`),
  generateKnockoutPhase: (championshipId, opts = {}) =>
    api.post(`/championships/${championshipId}/generate-knockout-phase`, null, {
      params: { force: true },
    }),
  knockoutPreview: (championshipId) =>
    api.get(`/championships/${championshipId}/knockout-preview`),
  alboDoro: () => api.get('/albo-doro'),
  alboLegacyList: () => api.get('/admin/albo-doro/legacy'),
  alboLegacyCreate: (body) => api.post('/admin/albo-doro/legacy', body),
  alboLegacyUpdate: (entryId, body) => api.put(`/admin/albo-doro/legacy/${entryId}`, body),
  alboLegacyDelete: (entryId) => api.delete(`/admin/albo-doro/legacy/${entryId}`),
};

// Match Chat
export const matchChatApi = {
  requestAdminHelp: (matchId) => api.post(`/matches/${matchId}/request-admin-help`),
};

// Calciomercato — Bacheca trasferimenti
export const marketBoardApi = {
  listAds: (params = {}) => api.get('/market/board/ads', { params }),
  myAds: () => api.get('/market/board/my-ads'),
  createAd: (body) => api.post('/market/board/ads', body),
  deleteAd: (adId) => api.delete(`/market/board/ads/${adId}`),
  sendFirstMessage: (adId, content) =>
    api.post(`/market/board/ads/${adId}/messages`, { content }),
  replyInThread: (adId, otherUserId, content) =>
    api.post(`/market/board/threads/${adId}/${otherUserId}/messages`, { content }),
  myThreads: () => api.get('/market/board/threads'),
  getThread: (adId, otherUserId) =>
    api.get(`/market/board/threads/${adId}/${otherUserId}`),
  unreadCount: () => api.get('/market/board/unread-count'),
};

// Admin Mediations Dashboard
export const mediationsApi = {
  list: () => api.get('/admin/mediations-pending'),
  count: () => api.get('/admin/mediations-pending/count'),
  resolve: (matchId) => api.post(`/admin/mediations/${matchId}/resolve`),
};

// Notifications
export const notificationsApi = {
  list: (params) => api.get('/notifications', { params }),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

export const pushApi = {
  getVapidKey: () => api.get('/push/vapid-key'),
  subscribe: (subscription) => api.post('/push/subscribe', subscription),
  unsubscribe: () => api.delete('/push/unsubscribe'),
};

// Admin
export const adminApi = {
  getUsers: (params) => api.get('/admin/users', { params }),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  bulkDeleteUsers: (userIds) => api.post('/admin/users/bulk-delete', { user_ids: userIds }),
  getDeletedUsers: (params) => api.get('/admin/users/trash', { params }),
  restoreUser: (id) => api.post(`/admin/users/${id}/restore`),
  bulkRestoreUsers: (userIds) => api.post('/admin/users/bulk-restore', { user_ids: userIds }),
  permanentDeleteUser: (id) => api.post(`/admin/users/${id}/permanent-delete`),
  modifyCoins: (data) => api.post('/admin/coins', data),
  createAdmin: (userId) => api.post('/admin/create-admin', null, { params: { user_id: userId } }),
  seedDemoData: () => api.post('/admin/seed-demo-data'),
  createClub: (data) => api.post('/admin/create-club', data),
  adminChangePassword: (userId, newPassword) => api.post('/auth/admin/change-password', { user_id: userId, new_password: newPassword }),
  createUser: (data) => api.post('/admin/users', data),
  addMemberToClub: (clubId, userId) => api.post(`/admin/clubs/${clubId}/add-member`, { user_id: userId }),
};

// Users - for contract system  
export const usersApi = {
  list: (params) => api.get('/admin/users', { params }),
  get: (userId) => api.get(`/users/${userId}`),
  getStats: (userId) => api.get(`/users/${userId}/stats`),
  getFreeAgents: (search) => api.get('/users/free-agents', { params: { search } }),
};

// Activity Logs
export const logsApi = {
  getLogs: (params) => api.get('/admin/logs', { params }),
  getActionTypes: () => api.get('/admin/logs/actions'),
  clearOldLogs: (daysOld) => api.delete('/admin/logs', { params: { days_old: daysOld } }),
  clearAllLogs: () => api.delete('/admin/logs', { params: { clear_all: true } }),
};

// Stats
export const statsApi = {
  global: () => api.get('/stats/global'),
  getStatus: () => api.get('/stats/status'),
  unlock: () => api.post('/stats/unlock'),
  getAdvanced: () => api.get('/stats/advanced'),
  upcomingMatches: (limit) => api.get('/stats/upcoming-matches', { params: { limit } }),
  recentResults: (limit) => api.get('/stats/recent-results', { params: { limit } }),
  topClubs: (limit) => api.get('/stats/top-clubs', { params: { limit } }),
  topScorersWeek: (limit = 10) => api.get('/stats/top-scorers-week', { params: { limit } }),
};

// Coins Shop
export const premiumApi = {
  info: (clubId) => api.get('/premium/info', { params: { club_id: clubId } }),
  buyWithCoins: (clubId) => api.post('/premium/buy-with-coins', { club_id: clubId }),
  checkout: (data) => api.post('/premium/checkout', data),
  advancedStats: (clubId) => api.get(`/clubs/${clubId}/advanced-stats`),
  uploadBanner: (clubId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/clubs/${clubId}/banner`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const coinsApi = {
  getPackages: () => api.get('/coins/packages'),
  createCheckout: (data) => api.post('/coins/checkout', data),
  getCheckoutStatus: (sessionId) => api.get(`/coins/checkout/status/${sessionId}`),
  getTransactions: () => api.get('/coins/transactions'),
};

// PayPal
export const paypalApi = {
  getClientId: () => api.get('/paypal/client-id'),
  createOrder: (data) => api.post('/paypal/create-order', data),
  captureOrder: (data) => api.post('/paypal/capture-order', data),
};

// Contracts
export const contractsApi = {
  getDurations: () => api.get('/contracts/durations'),
  create: (data) => api.post('/contracts', data),
  getSent: () => api.get('/contracts/sent'),
  getReceived: () => api.get('/contracts/received'),
  respond: (id, accept) => api.put(`/contracts/${id}/respond`, null, { params: { accept } }),
  cancel: (id) => api.delete(`/contracts/${id}`),
  getActive: () => api.get('/contracts/active'),
};

// Bot Phrases
export const botApi = {
  getPhrases: (command) => api.get('/bot/phrases', { params: command ? { command } : {} }),
  createPhrase: (data) => api.post('/bot/phrases', data),
  updatePhrase: (id, data) => api.put(`/bot/phrases/${id}`, data),
  deletePhrase: (id) => api.delete(`/bot/phrases/${id}`),
  seedPhrases: () => api.post('/bot/phrases/seed'),
};

// Banners
export const bannersApi = {
  getActive: (limit = 50) => api.get('/banners', { params: { limit } }),
  getAll: () => api.get('/admin/banners'),
  create: (data) => api.post('/admin/banners', data),
  update: (id, data) => api.put(`/admin/banners/${id}`, data),
  delete: (id) => api.delete(`/admin/banners/${id}`),
  toggle: (id) => api.post(`/admin/banners/${id}/toggle`),
};

// Ads Removal
export const adsApi = {
  getStatus: () => api.get('/ads/status'),
  remove: () => api.post('/ads/remove'),
};

export default api;
